"use client";

import Heading from "@afa-shared/afa-components/dist/Heading";
import { ContentLink } from "@components/Link/ContentLink";
import { Shortcut } from "@components/Shortcut";
import { shouldRemoveBorder } from "@utils/teaserHelper/teaserHelper";
import clsx from "clsx";

import { Context } from "types/context/Context";

import {
  ShortCutsBlockType_shortCutsBlock,
  ShortCutsBlockType_shortCutsBlock_linksCollection_items,
} from "./queries/contentfulTypes/ShortCutsBlockType";
import shortCutsStyles from "./shortCuts.module.css";

interface IShortCutsBlockProps {
  blockData: ShortCutsBlockType_shortCutsBlock;
  context?: Context;
}

export type ShortcutIconObject = {
  iconId: string;
  backgroundColor: string | null;
};

export const ShortCutsBlock = ({ blockData: shortCuts, context }: IShortCutsBlockProps) => {
  const hasColoredContext = shouldRemoveBorder(context);

  return (
    <section
      className={clsx(shortCutsStyles.shortCuts, {
        [shortCutsStyles.shortCutsInSection]: context?.sectionBlock && context?.columns === 1,
      })}
      data-testid="shortcuts-block"
      data-contentful-entry-id={shortCuts.sys.id}
      data-contentful-field-id="name"
    >
      {shortCuts?.heading?.trim() && (
        <Heading
          className={shortCutsStyles.heading}
          variant="h2"
          headingVariant={context?.heading?.parentLevel}
          removeMargin
        >
          {shortCuts.heading}
        </Heading>
      )}
      <ul
        className={clsx(shortCutsStyles.list, {
          [shortCutsStyles.oneColumns]: shortCuts.columns === 1,
          [shortCutsStyles.twoColumns]: shortCuts.columns === 2 || shortCuts.columns === null,
          [shortCutsStyles.threeColumns]: shortCuts.columns === 3,
        })}
        data-blockname="snabblänkar"
      >
        {shortCuts?.linksCollection?.items?.map(
          (item: ShortCutsBlockType_shortCutsBlock_linksCollection_items, index: number) => {
            const itemUrl = item?.url;
            const linkToInsurance = shortcutToWhatInsurance(itemUrl);

            return (
              <li key={index}>
                <ContentLink
                  href={itemUrl}
                  className={shortCutsStyles.link}
                  contentText={item?.linkText}
                  trackingData={{
                    sectionTitle:
                      context?.sectionBlock?.sectionBlockHeading ?? context?.pageHeading,
                    sectionBlockIndex: context?.sectionBlockIndex,
                    linkType: "shortcut",
                    insurance: linkToInsurance || undefined,
                  }}
                >
                  <Shortcut
                    icon={item?.shortcutIconObject as ShortcutIconObject}
                    label={item?.linkText}
                    text={item?.shortcutText}
                    removeContentBorder={hasColoredContext}
                    useIconBorder={hasColoredContext}
                  />
                </ContentLink>
              </li>
            );
          },
        )}
      </ul>
    </section>
  );
};

export function shortcutToWhatInsurance(url: string | null) {
  if (!url) return undefined;
  if (url.includes("/forsakring-vid-dodsfall")) return "tgl - tjänstegrupplivförsäkring";
  if (url.includes("/sjukforsakring")) return "ags - sjukforsäkring";
  if (url.includes("/arbetsskadeforsakring")) return "tfa/psa - arbetsskadeförsäkring";
  if (url.includes("/foraldrapenningtillagg")) return "fpt - föräldrapenningtillägg";
  if (url.includes("/forsakring-vid-arbetsbrist-agb")) return "agb - arbetsbrist";
  return undefined;
}

export default ShortCutsBlock;
